import React from "react";

import {
  formatSoftwareTierPrice,
  softwareFeatureRows,
  softwarePricingContent,
  softwarePricingTiers
} from "../content/softwarePricing";
import { getSoftwareTierStructuredMediaImage } from "../content/structuredMedia";
import type { Locale } from "../content/types";
import { dbkGetPublicSoftwareTier, type DbkBillingTierKey, type DbkPricingCountry } from "../../server/billing/dynamicBooksPublicPricing";
import { SoftwareRatingsWidget } from "./SoftwareRatingsWidget";
import { StructuredMediaFigure, StructuredMediaImg } from "./StructuredMediaFigure";

type SoftwarePricingSectionProps = {
  locale: Locale;
};

export function buildDynamicMailCheckoutHref(tierKey: string, country?: DbkPricingCountry): string {
  const params = new URLSearchParams({
    billing: "dynamicbooks",
    product_family: "dynamicbooks_accounting_software",
    dynamicbooks_tier: tierKey
  });
  if (country) {
    const tier = dbkGetPublicSoftwareTier(country, tierKey as DbkBillingTierKey);
    params.set("country_code", tier.country_code);
    params.set("currency_code", tier.currency_code);
  }
  return `https://owa.dynamicmail.ai/admin/dynamic-mail?${params.toString()}`;
}

export function SoftwarePricingSection({ locale }: SoftwarePricingSectionProps) {
  const content = softwarePricingContent[locale];
  const policyVisuals = buildPolicyVisuals(locale);

  return (
    <section className="db-section software-pricing" id={content.id} aria-labelledby="software-pricing-title">
      <div className="db-container">
        <div className="section-heading">
          <p className="section-eyebrow">{content.eyebrow}</p>
          <h2 id="software-pricing-title">{content.title}</h2>
          <p>{content.lead}</p>
        </div>

        <div className="software-tier-grid" aria-label={content.title}>
          {softwarePricingTiers.map((tier) => {
            const tierImage = getSoftwareTierStructuredMediaImage(tier.key);

            return (
              <article className="software-tier-card" data-software-tier={tier.key} key={tier.key}>
                <figure className="software-tier-card__media" data-structured-media={tierImage.assetId}>
                  <StructuredMediaImg media={tierImage} />
                </figure>
                <div className="software-tier-card__header">
                  <h3>{tier.name[locale]}</h3>
                  <p>{tier.summary[locale]}</p>
                </div>
                <p className="software-tier-card__price">
                  <span className="software-tier-card__price-chip" data-pricing-chip>
                    {formatSoftwareTierPrice(tier, locale)}
                  </span>
                  <small>{content.priceNote}</small>
                </p>
                <p className="software-tier-card__usage">{tier.includedUsage[locale]}</p>
                <ul>
                  {tier.highlights[locale].map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                  {tier.entitlementSummary[locale].map((entitlement) => (
                    <li key={entitlement}>{entitlement}</li>
                  ))}
                </ul>
                <a className="db-button db-button--secondary" href={buildDynamicMailCheckoutHref(tier.billingTierKey)}>
                  {content.ctaLabel}
                </a>
              </article>
            );
          })}
        </div>

        <div className="software-feature-matrix" aria-labelledby="software-feature-matrix-title">
          <h3 id="software-feature-matrix-title">{content.matrixTitle}</h3>
          <div className="software-feature-matrix__scroller">
            <table>
              <thead>
                <tr>
                  <th scope="col">{locale === "sv" ? "Funktion" : "Feature"}</th>
                  {softwarePricingTiers.map((tier) => (
                    <th scope="col" key={tier.key}>
                      {tier.name[locale]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {softwareFeatureRows.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.feature[locale]}</th>
                    {softwarePricingTiers.map((tier) => (
                      <td key={tier.key}>{row.values[tier.key][locale]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <section className="software-pricing-notes" aria-labelledby="software-pricing-notes-title">
          <h3 id="software-pricing-notes-title">{content.notesTitle}</h3>
          <div className="software-policy-visuals" aria-label={locale === "sv" ? "Policy och leverans" : "Policy and delivery"}>
            {policyVisuals.map((visual) => (
              <article className="software-policy-visual" key={visual.assetId}>
                <StructuredMediaFigure assetId={visual.assetId} className="software-policy-visual__media" />
                <div>
                  <h4>{visual.title}</h4>
                  <p>{visual.body}</p>
                </div>
              </article>
            ))}
          </div>
          <ul>
            {content.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </section>

        <SoftwareRatingsWidget locale={locale} />
      </div>
    </section>
  );
}

function buildPolicyVisuals(locale: Locale) {
  return [
    {
      assetId: "policy-digital-delivery" as const,
      title: locale === "sv" ? "Digital leverans" : "Digital delivery",
      body: locale === "sv"
        ? "Programvaruåtkomst provisioneras digitalt utan fysisk frakt."
        : "Software access is provisioned digitally with no physical shipment."
    },
    {
      assetId: "policy-no-return" as const,
      title: locale === "sv" ? "Provisionerad åtkomst" : "Provisioned access",
      body: locale === "sv"
        ? "Åtkomst som har provisionerats är inte en fysisk vara att returnera."
        : "Provisioned access is not a physical item to return."
    }
  ];
}
